import React, { useEffect, useRef, useState } from 'react';

import { jwtDecode } from 'jwt-decode';

import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { useSnackbar, useTableConfig } from 'hooks';

import CustomSpinner from 'components/shared/CustomSpinner';
import CustomEmpty from 'components/shared/CustomEmpty';

import { clearAuthSessionStorage } from 'core/storage/authSessionStorage';
import { HttpStatusCode, SYSTEM_ROLES } from 'constants/globals';

import { Token } from 'commons/modules/auth/token';

import { getRole, isSuperAdministrator } from 'utils/access';
import { sleep } from 'utils/async';
import { arrayValidation } from 'utils/array';

import UserService from 'service/auth';

import {
  getPermissionById,
  savePermission,
  updatePermission,
} from 'api/user/permissions';
import { getUserAccessConfigByEmail } from 'api/user/user-access';

import type { RouterType } from 'commons/modules/routers/routes';
import type {
  Permision,
  PermisionAccessRole,
  UserAccessOverride,
} from 'api/user/models';

import { setInvestmentOfficeId } from 'store/globalApp';

import UserRoleContext from './context';

import {
  validationPermissionsMenu,
  getNewPermissionsMenu,
  getSpecialOperations,
  applyExclusiveByOfficeToPermissions,
} from './helpers';

type Props = {
  children: React.ReactNode;
  runProcessInit: boolean;
};

const accessTypeToShowTheScreen = ['edit', 'readOnly'];

export const UserRoleProvider = ({ children, runProcessInit }: Props) => {
  const history = useHistory();
  const dispatch = useDispatch();
  const { showSnackbarError } = useSnackbar();
  const { loadTableConfigurations } = useTableConfig();
  const [role, setRole] = useState<string>('');
  const [clientId, setClientId] = useState<number | null>(null);
  const [accessByMenu, setAccessByMenu] = useState<Permision[]>([]);
  const [userAccess, setUserAccess] = useState<UserAccessOverride[]>([]);
  const savedPreviously = useRef(false);
  const updatedPreviously = useRef(false);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [isSpecialOperations, setSpecialOperations] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const waitForTheToken = async () => {
    let waitToken = true;
    const MAX_ATTEMPTS = 5;
    let count = 0;
    while (waitToken) {
      const token = UserService.getToken();

      if (!token && count < MAX_ATTEMPTS) {
        count += 1;
        // eslint-disable-next-line
        await sleep(500);
      } else {
        waitToken = false;
      }
    }
    return;
  };

  const handleLogOut = () => {
    if (window.location.pathname !== '/') {
      history.push('/');
    }
    clearAuthSessionStorage();
    UserService.doLogout();
  };

  const processGetUserRole = async () => {
    const getUserRole = () => {
      const data: Token = jwtDecode(String(token));
      const tempIsSuper = isSuperAdministrator(data);
      setIsSuperAdmin(tempIsSuper);
      setSpecialOperations(tempIsSuper || getSpecialOperations(data));

      const authorities = data?.realm_access?.roles || [];
      if (arrayValidation(authorities)) {
        const userRole = getRole(authorities);
        if (userRole && userRole !== SYSTEM_ROLES.otro) {
          setRole(userRole);
          setClientId(data.clientId);
          return;
        }
      }
      showSnackbarError(
        'No tienes acceso al sistema. Contacta al administrador',
      );
      handleLogOut();
    };

    const token = UserService.getToken();
    if (token) {
      getUserRole();
    } else {
      setErrorMessage(
        'No se pudo cargar el rol del usuario, Se recomienda recargar la página',
      );
    }
  };

  const processUserAccess = async () => {
    const email = String(UserService.getUsername?.() || '');
    const token = UserService.getToken();

    if (!token || !email) {
      setUserAccess([]);
      return;
    }

    const tokenData: Token = jwtDecode(String(token));
    const tempIsSuperAdmin = isSuperAdministrator(tokenData);
    if (tempIsSuperAdmin) {
      setUserAccess([]);
      return;
    }

    const resp = await getUserAccessConfigByEmail(email);
    if (resp && resp.success && resp.data?.data && resp.data.data.data) {
      setUserAccess(resp.data.data.data || []);
      return;
    }
    setUserAccess([]);
  };

  const updateAccessByMenu = (permissions: Permision[]) => {
    setAccessByMenu(applyExclusiveByOfficeToPermissions(permissions));
  };

  const processPermission = async () => {
    const resp = await getPermissionById();

    if (resp && resp.success && resp.data) {
      const respValidation = validationPermissionsMenu(
        resp.data.data.permissions,
      );
      if (respValidation.requiresUpdate && !updatedPreviously.current) {
        await updatePermission({ data: respValidation.accessByMenu });
        updatedPreviously.current = true;
        processPermission();
        return;
      }
      updateAccessByMenu(respValidation.accessByMenu);
      return;
    }

    if (resp.status === HttpStatusCode.NotFound && !savedPreviously.current) {
      savedPreviously.current = true;
      await savePermission({ data: getNewPermissionsMenu() });
      processPermission();
      return;
    }

    setErrorMessage(
      'No se pudo cargar los permisos, Se recomienda recargar la página',
    );
  };

  const getAccessByMenu = (route: RouterType) => {
    if (isSuperAdmin) return true;
    if (!arrayValidation(accessByMenu)) return false;
    const menu = accessByMenu.find((item) => item.menuKey === route.menuKey);
    if (!menu) return false;

    const screen = menu.screens.find(
      (item) => item.screenKey === route.screenKey,
    );

    if (!screen) return false;

    const itemRole = screen.accessRoles.find((item) => item.role === role);

    if (!itemRole) return false;

    return accessTypeToShowTheScreen.includes(itemRole.accessType);
  };

  const isOnlyQueryByAccessScreen = (
    screenKey: string,
    accessType: 'edit' | 'readOnly' | 'blocked' = 'readOnly',
  ) => {
    if (isSuperAdmin) return false;
    if (!screenKey) return true;
    if (!arrayValidation(accessByMenu)) return true;

    let itemRole: PermisionAccessRole | undefined;

    for (let index = 0; index < accessByMenu.length; index++) {
      const menu = accessByMenu[index];
      for (let indexMenu = 0; indexMenu < menu.screens.length; indexMenu++) {
        const screen = menu.screens[indexMenu];

        if (screen.screenKey === screenKey) {
          itemRole = screen.accessRoles.find((item) => item.role === role);
          break;
        }
      }
      if (itemRole) break;
    }

    if (!itemRole) return true;

    return itemRole.accessType === accessType;
  };

  const isAccessDeniedByScreen = (screenKey: string) => {
    return isOnlyQueryByAccessScreen(screenKey, 'blocked');
  };

  const syncInvestmentOffice = () => {
    const investmentOfficeId = UserService.getInvestmentOfficeId();
    dispatch(setInvestmentOfficeId(investmentOfficeId));
  };

  const processInit = async () => {
    setErrorMessage('');
    setIsLoading(true);
    await waitForTheToken();
    await processGetUserRole();
    await processPermission();
    await processUserAccess();
    setIsLoading(false);
    loadTableConfigurations();
    syncInvestmentOffice();
  };

  useEffect(() => {
    if (!runProcessInit) return;
    processInit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runProcessInit]);

  const value = React.useMemo(
    () => ({
      role,
      clientId,
      isSuperAdmin,
      accessByMenu,
      userAccess,
      isSpecialOperations,
      getAccessByMenu,
      updateAccessByMenu,
      isOnlyQueryByAccessScreen,
      isAccessDeniedByScreen,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      role,
      clientId,
      accessByMenu,
      userAccess,
      isSpecialOperations,
      isSuperAdmin,
    ],
  );

  if (runProcessInit && isLoading) {
    return (
      <>
        <CustomSpinner />
      </>
    );
  }

  if (runProcessInit && errorMessage) {
    return (
      <>
        <CustomEmpty messages={[errorMessage]} minHeight="80vh" />
      </>
    );
  }

  if (runProcessInit && !role) {
    return (
      <>
        <CustomEmpty
          messages={['No tienes acceso al sistema. Contacta al administrador']}
        />
      </>
    );
  }

  return (
    <UserRoleContext.Provider value={value}>
      {children}
    </UserRoleContext.Provider>
  );
};
